import { useEffect, useRef, useState } from 'react';

import { ImageWithFallback } from '@/components/ImageWithFallback';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { categoriesAPI, type Category } from '@/services/categories';
import { productAPI } from '@/services/products';
import { type EditableProductForm } from '@/types/product.types';
import type {
  CreateProductPayload,
  Product,
  UpdateProductPayload,
} from '@/types/products';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ArrowLeft, GripVertical, Plus, Save, Upload, X } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

interface ProductImageData {
  id?: string;
  url: string;
  sortOrder: number;
  isNew?: boolean;
  file?: File;
  fileName?: string; // Track original filename for duplicate detection
}

function mapApiToForm(p: Product): EditableProductForm {
  console.log(p);
  const sortedImages =
    p.images?.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)) ?? [];

  const attributes =
    p.attributes?.reduce(
      (acc, attr) => {
        acc[attr.key.key] = Array.isArray(attr.value)
          ? attr.value
          : [attr.value];
        return acc;
      },
      {} as Record<string, string[]>
    ) ?? {};

  return {
    name: p.name,
    sku: p.sku ?? '',
    categoryId:
      typeof p.category === 'string'
        ? ''
        : String(p.subCategory?.category?.id ?? ''),
    subCategoryId:
      typeof p.subCategory === 'object' && p.subCategory?.id
        ? String(p.subCategory.id)
        : undefined,
    price: p.price?.toString() || '',
    stock: '',
    description: p.description ?? '',
    width: attributes.width?.[0] ?? '',
    height: attributes.height?.[0] ?? '',
    depth: attributes.depth?.[0] ?? '',
    length: attributes.length?.[0] ?? '',
    thickness: attributes.thickness?.[0] ?? '',
    weight: '',
    materials: attributes.material?.[0] ?? '',
    manufacturer: '',
    warranty: p.warranty ?? '',
    status: p.isPublished ? 'active' : 'inactive',
    product_url: p.product_url ?? '',
    imageUrls: sortedImages
      .map((img) => img.originalUrl || img.thumbnailUrl)
      .filter((url): url is string => Boolean(url)),
    imageIds: sortedImages.map((img) => img.id),
    type: p.type ?? '',
    roomTypes: p.roomTypes ?? [],
    colors: attributes.colors ?? [],
    style: attributes.style?.[0] ?? '',
  };
}

function mapFormToPayload(
  form: EditableProductForm
): Omit<CreateProductPayload, 'images'> {
  return {
    name: form.name,
    sku: form.sku?.trim() || '',
    product_url: form.product_url,
    description: form.description,
    type: form.type || undefined,
    subCategoryId: form.subCategoryId ? Number(form.subCategoryId) : undefined,
    warranty: form.warranty || undefined,
    roomTypes:
      form.roomTypes && form.roomTypes.length > 0 ? form.roomTypes : undefined,
    width: form.width || undefined,
    height: form.height || undefined,
    depth: form.depth || undefined,
    length: form.length || undefined,
    thickness: form.thickness || undefined,
    material: form.materials || undefined,
    colors: form.colors && form.colors.length > 0 ? form.colors : undefined,
    style: form.style || undefined,
  };
}

export function ProductAddEdit() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const mode: 'add' | 'edit' = id ? 'edit' : 'add';

  const { data: existingProduct, isLoading: isLoadingProduct } = useQuery({
    queryKey: id ? ['product', id] : ['product', null],
    queryFn: () => (id ? productAPI.getProductById(id) : Promise.resolve(null)),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });

  const [formData, setFormData] = useState<EditableProductForm>({
    name: '',
    categoryId: '',
    subCategoryId: undefined,
    price: '',
    stock: '',
    description: '',
    width: '',
    height: '',
    depth: '',
    length: '',
    thickness: '',
    weight: '',
    materials: '',
    sku: '',
    manufacturer: '',
    warranty: '',
    status: 'active',
    product_url: '',
    imageUrls: [],
    imageIds: [],
    type: '',
    roomTypes: [],
    colors: [],
    style: '',
  });

  const [imageData, setImageData] = useState<ProductImageData[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<number, number>>(
    {}
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const [categoryPage, setCategoryPage] = useState(1);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryTotalPages, setCategoryTotalPages] = useState(1);

  const { data: categoriesResponse, isFetching: isFetchingCategories } =
    useQuery({
      queryKey: ['categories', { page: categoryPage, limit: 20 }],
      queryFn: () =>
        categoriesAPI.getCategories({ page: categoryPage, limit: 20 }),
      placeholderData: keepPreviousData,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    });

  useEffect(() => {
    if (categoriesResponse?.data) {
      setCategories((prev) => {
        const existingIds = new Set(prev.map((c) => c.id));
        const merged = [...prev];
        for (const c of categoriesResponse.data) {
          if (!existingIds.has(c.id)) merged.push(c);
        }
        return merged;
      });
      setCategoryTotalPages(categoriesResponse.totalPages ?? 1);
    }
  }, [categoriesResponse]);

  useEffect(() => {
    if (mode === 'edit' && existingProduct) {
      const mappedForm = mapApiToForm(existingProduct as Product);
      setFormData(mappedForm);

      const sortedImages =
        existingProduct.images?.sort(
          (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
        ) ?? [];

      setImageData(
        sortedImages.map((img, index) => ({
          id: img.id,
          url: img.originalUrl || img.thumbnailUrl || '',
          sortOrder: index,
          isNew: false,
          fileName: img.fileName || undefined, // Preserve server filename for duplicate checking
        }))
      );
    }
  }, [mode, existingProduct]);

  const { mutateAsync: createProduct, isPending: isCreating } = useMutation({
    mutationFn: async (payload: Omit<CreateProductPayload, 'images'>) => {
      const finalPayload: CreateProductPayload = {
        ...payload,
        images: imageData
          .filter((img) => img.id) // Only include images that have been uploaded (have ID)
          .map((img, index) => ({
            id: img.id!,
            order: index,
          })),
      };
      return productAPI.createProduct(finalPayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product created successfully!');
      navigate('/products-management');
    },
    onError: (err: unknown) => {
      const message = (err as Error)?.message ?? 'Failed to create product';
      setSubmitError(message);
      toast.error(message);
    },
  });

  const { mutateAsync: updateProduct, isPending: isUpdating } = useMutation({
    mutationFn: async (payload: UpdateProductPayload) => {
      const finalPayload: UpdateProductPayload = {
        ...payload,
        images: imageData
          .filter((img) => img.id) // Only include images that have IDs (uploaded or existing)
          .map((img, index) => ({
            id: img.id!,
            order: index,
          })),
      };

      const result = await productAPI.updateProduct(id as string, finalPayload);

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product updated successfully!');
      navigate('/products-management');
    },
    onError: (err: unknown) => {
      const message = (err as Error)?.message ?? 'Failed to update product';
      setSubmitError(message);
      toast.error(message);
    },
  });

  const handleInputChange = (
    field: keyof EditableProductForm,
    value: string | string[]
  ) => {
    setSubmitError(null);
    setFormData((prev) => ({ ...prev, [field]: value }) as EditableProductForm);
  };

  /**
   * Client-side duplicate check against currently loaded images
   * Checks by filename and size to prevent obvious duplicates in current session
   * Server-side duplicate prevention via fileName provides additional protection
   */
  const isDuplicateFile = (newFile: File): boolean => {
    // Check against currently selected new files (by name and size)
    const duplicateInNewFiles = imageData.some((existingImg) => {
      if (!existingImg.file) return false;
      return (
        existingImg.file.name === newFile.name &&
        existingImg.file.size === newFile.size
      );
    });

    if (duplicateInNewFiles) return true;

    // Check against existing images from server using stored fileName
    const duplicateInExistingImages = imageData.some((existingImg) => {
      if (existingImg.isNew || !existingImg.url) return false;

      // First priority: check against stored fileName from backend
      if (existingImg.fileName) {
        // Backend may store filename without timestamp prefix
        // Compare the actual filename parts
        const storedName = existingImg.fileName.toLowerCase();
        const newName = newFile.name.toLowerCase();

        // Direct match
        if (storedName === newName) return true;

        // Check if stored name ends with the new filename (handles timestamp prefixes)
        if (storedName.endsWith(newName)) return true;

        // Check if new name matches the end of stored name (handles various prefixes)
        if (storedName.includes(newName)) return true;
      }

      // Fallback: Extract filename from URL for older images without fileName
      const urlFilename =
        existingImg.url.split('/').pop()?.split('?')[0]?.toLowerCase() || '';
      const newFilename = newFile.name.toLowerCase();

      // Check if filenames match (handles timestamp prefixes like "1699123456-image.jpg")
      if (urlFilename === newFilename) return true;
      if (urlFilename.includes(newFilename)) return true;
      if (urlFilename.split('-').pop() === newFilename) return true;

      return false;
    });

    return duplicateInExistingImages;
  };

  const handleImageUpload = async (files: FileList) => {
    try {
      setIsUploading(true);
      const fileArray = Array.from(files);
      const newImages: ProductImageData[] = [];
      const duplicateFiles: string[] = [];
      const failedFiles: string[] = [];

      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        const fileIndex = imageData.length + newImages.length;

        if (isDuplicateFile(file)) {
          duplicateFiles.push(file.name);
          continue;
        }

        if (imageData.length + newImages.length >= 10) {
          toast.warning('Maximum of 10 images allowed per product');
          break;
        }

        try {
          setUploadProgress((prev) => ({
            ...prev,
            [fileIndex]: 0,
          }));

          // Upload image to API
          const sortOrder = imageData.length + newImages.length;
          const uploadResponse = await productAPI.uploadProductImage(
            file,
            sortOrder
          );

          setUploadProgress((prev) => ({
            ...prev,
            [fileIndex]: 50,
          }));

          // Use the URL from API response (prefer thumbnailUrl, fallback to originalUrl)
          const imageUrl =
            uploadResponse.thumbnailUrl || uploadResponse.originalUrl;

          setUploadProgress((prev) => ({
            ...prev,
            [fileIndex]: 100,
          }));

          // Clean up progress after a delay
          setTimeout(() => {
            setUploadProgress((current) => {
              const updated = { ...current };
              delete updated[fileIndex];
              return updated;
            });
          }, 500);

          newImages.push({
            id: uploadResponse.id,
            url: imageUrl,
            sortOrder: uploadResponse.sortOrder,
            isNew: true,
            fileName: file.name,
          });
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
          failedFiles.push(file.name);
          setUploadProgress((prev) => {
            const updated = { ...prev };
            delete updated[fileIndex];
            return updated;
          });
        }
      }

      if (duplicateFiles.length > 0) {
        toast.warning(
          `${duplicateFiles.length} duplicate file(s) skipped: ${duplicateFiles.slice(0, 3).join(', ')}${duplicateFiles.length > 3 ? '...' : ''}`
        );
      }
      if (failedFiles.length > 0) {
        toast.error(`Failed to upload: ${failedFiles.join(', ')}`);
      }
      if (newImages.length > 0) {
        toast.success(`${newImages.length} image(s) uploaded successfully`);
      }

      if (newImages.length > 0) {
        setImageData((prev) => [...prev, ...newImages]);
        setFormData((prev) => ({
          ...prev,
          imageUrls: [...prev.imageUrls, ...newImages.map((img) => img.url)],
        }));
      }
    } catch (error) {
      console.error('Error during file upload:', error);
      setSubmitError(
        'An error occurred while uploading files. Please try again.'
      );
      toast.error('Failed to upload images');
    } finally {
      setIsUploading(false);
    }
  };

  const removeImage = (index: number) => {
    const imageToRemove = imageData[index];

    // Clean up blob URL if it exists (for preview purposes)
    if (imageToRemove?.url && imageToRemove.url.startsWith('blob:')) {
      URL.revokeObjectURL(imageToRemove.url);
    }

    // Remove image from state - it won't be included in the images array when updating
    setImageData((prev) => prev.filter((_, i) => i !== index));
    setFormData((prev) => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((_, i) => i !== index),
    }));
  };

  const moveImage = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    setImageData((prev) => {
      const newImages = [...prev];
      const [movedImage] = newImages.splice(fromIndex, 1);
      newImages.splice(toIndex, 0, movedImage);

      return newImages.map((img, index) => ({
        ...img,
        sortOrder: index,
      }));
    });

    setFormData((prev) => {
      const newImageUrls = [...prev.imageUrls];
      const newImageIds = [...(prev.imageIds || [])];

      const [movedUrl] = newImageUrls.splice(fromIndex, 1);
      const [movedId] = newImageIds.splice(fromIndex, 1);

      newImageUrls.splice(toIndex, 0, movedUrl);
      newImageIds.splice(toIndex, 0, movedId);

      return {
        ...prev,
        imageUrls: newImageUrls,
        imageIds: newImageIds,
      };
    });
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (!isNaN(dragIndex)) {
      moveImage(dragIndex, dropIndex);
    }
    setDraggedIndex(null);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const remainingSlots = 10 - imageData.length;
      if (remainingSlots <= 0) {
        setSubmitError('Maximum of 10 images allowed per product');
        toast.error('Maximum of 10 images allowed per product');
        return;
      }

      const files = Array.from(e.target.files).slice(0, remainingSlots);
      if (files.length < e.target.files.length) {
        const message = `You can only upload ${remainingSlots} more image(s)`;
        setSubmitError(message);
        toast.warning(message);
      }

      const fileList = new DataTransfer();
      files.forEach((file) => fileList.items.add(file));

      handleImageUpload(fileList.files);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!formData.name.trim()) {
      setSubmitError('Product name is required');
      return;
    }
    if (!formData.product_url.trim()) {
      setSubmitError('Product URL is required');
      return;
    }

    try {
      const isEdit = mode === 'edit';

      if (isEdit) {
        const payload: UpdateProductPayload = {
          name: formData.name,
          sku: formData.sku || undefined,
          product_url: formData.product_url,
          description: formData.description,
          type: formData.type,
          subCategoryId: formData.subCategoryId
            ? Number(formData.subCategoryId)
            : undefined,
          warranty: formData.warranty,
          roomTypes: formData.roomTypes,
        };
        await updateProduct(payload);
      } else {
        const payload = mapFormToPayload(formData);
        await createProduct(payload);
      }
    } catch (error) {
      console.error('Submit error:', error);
    }
  };

  const title = mode === 'add' ? 'Add New Product' : 'Edit Product';

  useEffect(() => {
    return () => {
      imageData.forEach((img) => {
        if (img.isNew && img.url.startsWith('blob:')) {
          URL.revokeObjectURL(img.url);
        }
      });
    };
  }, [imageData]);

  const [roomTypeInput, setRoomTypeInput] = useState('');
  const [colorInput, setColorInput] = useState('');
  const [showRoomTypeInput, setShowRoomTypeInput] = useState(false);
  const [showColorInput, setShowColorInput] = useState(false);

  const addRoomType = () => {
    if (roomTypeInput.trim()) {
      const trimmedInput = roomTypeInput.trim();
      if (!(formData.roomTypes || []).includes(trimmedInput)) {
        setFormData((prev) => ({
          ...prev,
          roomTypes: [...(prev.roomTypes || []), trimmedInput],
        }));
        setRoomTypeInput('');
        setShowRoomTypeInput(false);
        toast.success(`Room type "${trimmedInput}" added`);
      } else {
        toast.warning('Room type already exists');
      }
    }
  };

  const removeRoomType = (index: number) => {
    const removedType = formData.roomTypes?.[index];
    setFormData((prev) => ({
      ...prev,
      roomTypes: prev.roomTypes?.filter((_, i) => i !== index) || [],
    }));
    if (removedType) {
      toast.success(`Room type "${removedType}" removed`);
    }
  };

  const addColor = () => {
    if (colorInput.trim()) {
      const trimmedInput = colorInput.trim();
      if (!(formData.colors || []).includes(trimmedInput)) {
        setFormData((prev) => ({
          ...prev,
          colors: [...(prev.colors || []), trimmedInput],
        }));
        setColorInput('');
        setShowColorInput(false);
        toast.success(`Color "${trimmedInput}" added`);
      } else {
        toast.warning('Color already exists');
      }
    }
  };

  const removeColor = (index: number) => {
    const removedColor = formData.colors?.[index];
    setFormData((prev) => ({
      ...prev,
      colors: prev.colors?.filter((_, i) => i !== index) || [],
    }));
    if (removedColor) {
      toast.success(`Color "${removedColor}" removed`);
    }
  };

  const handleRoomTypeKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addRoomType();
    } else if (e.key === 'Escape') {
      setRoomTypeInput('');
      setShowRoomTypeInput(false);
    }
  };

  const handleColorKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addColor();
    } else if (e.key === 'Escape') {
      setColorInput('');
      setShowColorInput(false);
    }
  };

  return (
    <div className=" space-y-6">
      {submitError && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}
      {/* Header */}
      <div className="flex flex-col-reverse sm:flex-row gap-4 md:items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="flex items-center"
          >
            <ArrowLeft className="h-4 w-4 md:mr-2" />
            <span className="hidden md:block">Back to Products</span>
          </Button>
          <div className="h-6 w-px bg-border" />
          <h1 className="text-2xl font-semibold">{title}</h1>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex items-center"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex items-center"
            disabled={
              isCreating || isUpdating || (mode === 'edit' && isLoadingProduct)
            }
          >
            <Save className="h-4 w-4 mr-2" />
            {mode === 'add' ? 'Add Product' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <Card>
            <CardContent className="space-y-4 pt-5">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="productUrl">Product URL</Label>
                  <Input
                    id="productUrl"
                    value={formData.product_url}
                    onChange={(e) =>
                      handleInputChange('product_url', e.target.value)
                    }
                    placeholder="https://example.com/ergonomic-chair"
                  />
                </div>
              </div>

              <div className="aspect-square w-full p-2 h-[400px] rounded-lg overflow-hidden bg-muted">
                {imageData[0] ? (
                  <ImageWithFallback
                    src={imageData[0].url}
                    alt={formData.name}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    No image available
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Product Images (Drag to reorder)</Label>
                <div className="grid grid-cols-4 gap-2 items-center">
                  {imageData.map((img, index) => (
                    <div
                      key={`${img.id || index}-${img.url}`}
                      className={`relative group cursor-move  h-[100px] ${
                        draggedIndex === index ? 'opacity-50' : ''
                      }`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                    >
                      <div className=" rounded-md p-1 overflow-hidden h-[100px] border border-border relative">
                        <ImageWithFallback
                          src={img.url}
                          alt={`Product image ${index + 1}`}
                          className="w-full h-full object-contain"
                        />
                        <div className="absolute top-1 left-1 bg-black/50 text-white text-xs px-1 rounded">
                          {index + 1}
                        </div>
                        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <GripVertical className="h-4 w-4 text-white drop-shadow" />
                        </div>
                        {img.isNew && (
                          <div className="absolute bottom-1 left-1 bg-blue-500 text-white text-xs px-1 rounded">
                            New
                          </div>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => removeImage(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}

                  {imageData.length < 10 && (
                    <div className="  h-[100px]">
                      <input
                        type="file"
                        ref={fileInputRef}
                        multiple
                        accept="image/*"
                        onChange={handleFileInputChange}
                        className="hidden"
                        disabled={isUploading}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-full flex flex-col items-center justify-center border-2 border-dashed border-border rounded-md hover:bg-accent/50 transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                      >
                        <Upload className="h-6 w-6 mb-2 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {isUploading ? 'Processing...' : 'Add Images'}
                        </span>
                      </Button>
                    </div>
                  )}
                </div>

                {Object.entries(uploadProgress).map(([index, progress]) => (
                  <div key={index} className="space-y-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Uploading image {parseInt(index) + 1}...</span>
                      <span>{progress}%</span>
                    </div>
                    <Progress value={progress} className="h-1.5" />
                  </div>
                ))}

                <p className="text-xs text-muted-foreground mt-1">
                  Upload up to 10 images ({imageData.length}/10). Drag to
                  reorder. Recommended size: 800x800px or larger.
                </p>
              </div>
              {imageData.length > 0 && (
                <div className="space-y-2">
                  <Label>Image Summary</Label>
                  <div className="text-sm text-muted-foreground bg-muted p-2 rounded">
                    {imageData.length} image(s) selected
                    {imageData.filter((img) => img.isNew).length > 0 && (
                      <span className="ml-2 text-blue-600">
                        ({imageData.filter((img) => img.isNew).length} new)
                      </span>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Product Information Form */}
        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Product Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter product name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sku">SKU</Label>
                  <Input
                    id="sku"
                    value={formData.sku}
                    onChange={(e) => handleInputChange('sku', e.target.value)}
                    placeholder="Enter SKU (optional)"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.categoryId}
                    onValueChange={(value) => {
                      if (value === '__see_more__') {
                        if (categoryPage < categoryTotalPages)
                          setCategoryPage((p) => p + 1);
                        return;
                      }
                      handleInputChange('categoryId', value);
                      handleInputChange('subCategoryId', '');
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => {
                        const name =
                          typeof cat.name === 'string'
                            ? cat.name
                            : (cat.name.en ??
                              cat.name.uz ??
                              cat.name.ru ??
                              `Category ${cat.id}`);
                        return (
                          <SelectItem key={cat.id} value={String(cat.id)}>
                            {name}
                          </SelectItem>
                        );
                      })}
                      {categoryPage < categoryTotalPages && (
                        <SelectItem
                          value="__see_more__"
                          disabled={isFetchingCategories}
                        >
                          {isFetchingCategories ? 'Loading...' : 'See more'}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subCategory">Sub Category</Label>
                  <Select
                    value={formData.subCategoryId ?? ''}
                    onValueChange={(value) =>
                      handleInputChange('subCategoryId', value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select sub category" />
                    </SelectTrigger>
                    <SelectContent>
                      {(
                        categories.find(
                          (c) => String(c.id) === formData.categoryId
                        )?.subCategories ?? []
                      ).map((sub) => {
                        const name =
                          typeof sub.name === 'string'
                            ? sub.name
                            : (sub.name.en ??
                              sub.name.uz ??
                              sub.name.ru ??
                              `Sub ${sub.id}`);
                        return (
                          <SelectItem key={sub.id} value={String(sub.id)}>
                            {name}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Product Type</Label>
                  <Input
                    id="type"
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    placeholder="e.g., Furniture, Decor"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="style">Style</Label>
                  <Input
                    id="style"
                    value={formData.style}
                    onChange={(e) => handleInputChange('style', e.target.value)}
                    placeholder="e.g., Modern, Classic"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Description & Room Types */}
          <Card>
            <CardHeader>
              <CardTitle>Description & Room Types</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange('description', e.target.value)
                  }
                  rows={3}
                  placeholder="Describe the product features, materials, and benefits..."
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Room Types</Label>
                  {!showRoomTypeInput && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowRoomTypeInput(true)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Room Type
                    </Button>
                  )}
                </div>

                {showRoomTypeInput && (
                  <div className="flex gap-2">
                    <Input
                      value={roomTypeInput}
                      onChange={(e) => setRoomTypeInput(e.target.value)}
                      onKeyDown={handleRoomTypeKeyPress}
                      placeholder="Enter room type (e.g., Living Room, Bedroom)"
                      className="flex-1"
                      autoFocus
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={addRoomType}
                      disabled={!roomTypeInput.trim()}
                    >
                      Add
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setRoomTypeInput('');
                        setShowRoomTypeInput(false);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {formData.roomTypes && formData.roomTypes.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {(formData.roomTypes || []).map((roomType, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="flex items-center gap-1 hover:bg-secondary/80 transition-colors"
                      >
                        {roomType}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 ml-1 hover:bg-destructive hover:text-destructive-foreground rounded-full"
                          onClick={() => removeRoomType(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                )}

                {!formData.roomTypes?.length && !showRoomTypeInput && (
                  <p className="text-sm text-muted-foreground">
                    No room types added. Click "Add Room Type" to specify where
                    this product can be used.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Specifications */}
          <Card>
            <CardHeader>
              <CardTitle>Specifications</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="width">Width (ft)</Label>
                  <Input
                    id="width"
                    value={formData.width}
                    onChange={(e) => handleInputChange('width', e.target.value)}
                    placeholder="e.g., 4"
                    type="number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="height">Height (ft)</Label>
                  <Input
                    id="height"
                    value={formData.height}
                    onChange={(e) =>
                      handleInputChange('height', e.target.value)
                    }
                    placeholder="e.g., 3"
                    type="number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="depth">Depth (cm)</Label>
                  <Input
                    id="depth"
                    value={formData.depth}
                    onChange={(e) => handleInputChange('depth', e.target.value)}
                    placeholder="e.g., 60"
                    type="number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="length">Length (cm)</Label>
                  <Input
                    id="length"
                    value={formData.length}
                    onChange={(e) =>
                      handleInputChange('length', e.target.value)
                    }
                    placeholder="e.g., 200"
                    type="number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="materials">Material</Label>
                  <Input
                    id="materials"
                    value={formData.materials}
                    onChange={(e) =>
                      handleInputChange('materials', e.target.value)
                    }
                    placeholder="e.g., Solid Wood, Metal"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="warranty">Warranty</Label>
                  <Input
                    id="warranty"
                    value={formData.warranty}
                    onChange={(e) =>
                      handleInputChange('warranty', e.target.value)
                    }
                    placeholder="e.g., 2 years"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Colors Available</Label>
                  {!showColorInput && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowColorInput(true)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Color
                    </Button>
                  )}
                </div>

                {showColorInput && (
                  <div className="flex gap-2">
                    <Input
                      value={colorInput}
                      onChange={(e) => setColorInput(e.target.value)}
                      onKeyDown={handleColorKeyPress}
                      placeholder="Enter color (e.g., White, Black, Natural Wood)"
                      className="flex-1"
                      autoFocus
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={addColor}
                      disabled={!colorInput.trim()}
                    >
                      Add
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setColorInput('');
                        setShowColorInput(false);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {formData.colors && formData.colors.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.colors.map((color, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className="flex items-center gap-1 hover:bg-accent transition-colors"
                      >
                        <div
                          className="w-3 h-3 rounded-full border border-border mr-1"
                          style={{
                            backgroundColor: color.toLowerCase().includes('#')
                              ? color
                              : color.toLowerCase().replace(/\s+/g, ''),
                          }}
                        />
                        {color}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 ml-1 hover:bg-destructive hover:text-destructive-foreground rounded-full"
                          onClick={() => removeColor(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                )}

                {!formData.colors?.length && !showColorInput && (
                  <p className="text-sm text-muted-foreground">
                    No colors added. Click "Add Color" to specify available
                    color options.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
